import React from "react";
import PropTypes from "prop-types";
import { Box } from "@material-ui/core";
import { Refresh } from "@material-ui/icons";
import { BRAND } from "src/theme/brandTokens";
import { BotonPrimario } from "./_botones";

// Resuelve título / mensaje / etiqueta del CTA en función del error recibido.
// La lógica replica la del componente legacy (sin conexión / 401-403 / 404 /
// otro). Solo cambia el shape del eyebrow contextual.
function resolverDetalleError(error) {
  if (!navigator.onLine) {
    return {
      eyebrow: "Sin conexión",
      titulo: "Sin conexión a Internet",
      mensaje:
        "Por favor, verifica tu conexión a Internet e intenta nuevamente.",
      cta: "Reintentar",
    };
  }
  if (error?.status === 401 || error?.status === 403) {
    return {
      eyebrow: "Enlace inválido",
      titulo: "Enlace inválido o expirado",
      mensaje:
        "El enlace que estás usando no es válido o ya expiró. Contacta a tu vendedor para recibir un enlace actualizado.",
      cta: "Reintentar",
    };
  }
  if (error?.status === 404) {
    return {
      eyebrow: "Propuesta no encontrada",
      titulo: "Propuesta no encontrada",
      mensaje:
        "La propuesta que buscas no existe o fue eliminada. Verifica el enlace o contacta a tu vendedor.",
      cta: "Volver a intentar",
    };
  }
  return {
    eyebrow: "Error",
    titulo: "Error al cargar la propuesta",
    mensaje:
      "Ocurrió un error al obtener los datos. Por favor, intenta nuevamente más tarde.",
    cta: "Reintentar",
  };
}

const ErrorViewV2 = ({ error, onRetry }) => {
  const detalle = resolverDetalleError(error);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: BRAND.color.grey10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 3, md: 4 },
        fontFamily: BRAND.font.family.base,
      }}
    >
      <Box
        sx={{
          position: "relative",
          maxWidth: 520,
          width: "100%",
          bgcolor: BRAND.color.paper,
          p: { xs: 4, md: 6 },
          pr: { xs: 4, md: 6 },
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 64,
            height: 64,
            bgcolor: BRAND.color.primary,
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, md: 3 },
            pr: "84px",
          }}
        >
          <Box
            sx={{
              fontSize: BRAND.font.size.eyebrow,
              color: BRAND.color.primary,
              textTransform: "uppercase",
              letterSpacing: BRAND.font.letterSpacing.widest,
              fontWeight: BRAND.font.weight.medium,
            }}
          >
            {detalle.eyebrow}
          </Box>
          <Box
            component="h2"
            sx={{
              m: 0,
              fontFamily: BRAND.font.family.display,
              fontSize: { xs: "22pt", md: BRAND.font.size.h2 },
              fontWeight: BRAND.font.weight.black,
              color: BRAND.color.inkSoft,
              lineHeight: BRAND.font.lineHeight.snug,
              letterSpacing: BRAND.font.letterSpacing.tight,
            }}
          >
            {detalle.titulo}
          </Box>
          <Box
            sx={{
              fontSize: BRAND.font.size.body,
              color: BRAND.color.grey70,
              lineHeight: BRAND.font.lineHeight.body,
            }}
          >
            {detalle.mensaje}
          </Box>
          {onRetry && (
            <Box sx={{ mt: 1 }}>
              <BotonPrimario onClick={onRetry}>
                <Refresh fontSize="small" />
                {detalle.cta}
              </BotonPrimario>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

ErrorViewV2.propTypes = {
  error: PropTypes.shape({
    status: PropTypes.number,
    data: PropTypes.shape({
      message: PropTypes.string,
    }),
  }),
  onRetry: PropTypes.func,
};

ErrorViewV2.defaultProps = {
  error: null,
  onRetry: null,
};

export default ErrorViewV2;
